import { ExternalLink, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import {
  MAP_INFO_CARD_BODY_CLASS,
  MAP_INFO_CARD_CLASS,
} from '@/features/map/components/mapBarGeometry';
import { useMixpanel } from '@/hooks/useMixpanel';
import { cn } from '@/libs/utils/cn';
import { MAP_DETAIL_CARD_WIDTH } from '../constants';
import { usePlaceAddress } from '../hooks/usePlaceAddress';
import { PlaceMark } from './PlaceMark';
import { usePlaceVerificationQuery } from '../services/placeVerification.api';
import { getGoogleMapUrl } from '../utils/getGoogleMapUrl';
import {
  POI_MIXPANEL_EVENTS,
  type PoiCreateLocationClickedPayload,
  type PoiGoogleMapsOpenedPayload,
  poiPlacePayload,
} from '../utils/mixpanelEvents';
import { placeTypeLabel } from '../utils/placeType';
import type { PointOfInterestPlace } from '../utils/pointOfInterest';

/** One height for the loading line and both chips, so the card does not resize. */
const placeStatusBox =
  'inline-flex h-[23px] shrink-0 items-center whitespace-nowrap text-[11px] leading-none';

/**
 * The place card, under the search column like a deal or a location.
 * The corner mark is the poi-label sprite: the same plate and glyph the map draws.
 * Google's status sits on the right, with space between it and the marker.
 * The name follows. The street sits under it, small, like a deal.
 * Then the type, as plain text.
 */
export function PointOfInterestPopup({
  place,
  onClose,
  onCreateLocation,
  dialogContainer,
}: {
  place: PointOfInterestPlace;
  onClose: () => void;
  /** Absent when the map has no workspace to add the location to. */
  onCreateLocation?: () => void;
  dialogContainer?: HTMLElement;
}) {
  const verification = usePlaceVerificationQuery(place);
  const verifying = verification.isFetching;
  const verified = !verifying && verification.data?.verified === true;
  const missed = !verifying && verification.data?.verified === false;
  const typeLabel = placeTypeLabel(place.maki);
  const addressQuery = usePlaceAddress(place.longitude, place.latitude);
  const addressLine = addressQuery.isPending ? '…' : (addressQuery.data ?? '—');
  const trackAction = useMixpanel((s) => s.trackAction);

  function createLocation() {
    const payload: PoiCreateLocationClickedPayload = poiPlacePayload(place);
    trackAction(POI_MIXPANEL_EVENTS.CREATE_LOCATION_CLICKED, payload);
    onCreateLocation?.();
  }

  function openGoogleMaps() {
    const payload: PoiGoogleMapsOpenedPayload = {
      ...poiPlacePayload(place),
      // The spinner is up while a cached answer refreshes, so that click is not a verdict yet.
      ...(!verifying && verification.isSuccess ? { is_verified: verification.data.verified } : {}),
    };
    trackAction(POI_MIXPANEL_EVENTS.GOOGLE_MAPS_OPENED, payload);
  }

  return (
    <Dialog
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        dialogContainer={dialogContainer}
        showOverlay={false}
        showClose={false}
        position="tl"
        className={cn(
          MAP_INFO_CARD_CLASS,
          'border-0 bg-transparent p-0 shadow-none',
          MAP_DETAIL_CARD_WIDTH,
        )}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <div
          className={cn(
            MAP_INFO_CARD_BODY_CLASS,
            'flex h-fit w-full flex-col rounded-[14px] border border-[#E5E6E6] bg-white p-3.5 shadow-[0_10px_26px_rgba(25,84,94,0.18),0_2px_6px_rgba(25,84,94,0.1)]',
          )}
        >
          <div className="flex min-h-[23px] items-center justify-between gap-3">
            <PlaceMark place={place} />
            <div className="flex shrink-0 items-center gap-1.5">
              {verifying && (
                <span role="status" className={cn(placeStatusBox, 'gap-1.5 text-primary-500')}>
                  <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-brand-800" />
                  Verifying on Google
                </span>
              )}
              {verified && (
                <span
                  className={cn(
                    placeStatusBox,
                    'rounded-full border border-[#006369]/25 bg-[#EBFBF9] px-2 font-bold text-[#006369]',
                  )}
                >
                  Google verified
                </span>
              )}
              {missed && (
                <span
                  className={cn(
                    placeStatusBox,
                    'rounded-full border border-warning-border bg-warning-bg px-2 font-bold text-warning-fg',
                  )}
                >
                  Google not found
                </span>
              )}
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="rounded-md p-0.5 text-[#626968] hover:text-[#021715] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </div>
          </div>

          <DialogTitle className="mt-[9px] text-[15px] font-bold leading-5 text-[#021715]">
            {place.name}
          </DialogTitle>
          <p
            className="mt-0.5 truncate text-[11px] leading-[15px] text-[#626968]"
            title={addressQuery.data ?? undefined}
          >
            {addressLine}
          </p>
          <span className="mt-[9px] block truncate text-xs leading-4 text-[#021715]">
            {`Type : ${typeLabel}`}
          </span>

          <div className="mt-[13px] flex gap-[7px]">
            <button
              type="button"
              disabled={onCreateLocation == null}
              onClick={createLocation}
              className="inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-full bg-[#0B2A37] px-2.5 py-[7px] text-[11px] font-bold leading-4 text-white hover:bg-[#061C25] disabled:opacity-50"
            >
              Create location
            </button>
            <a
              href={getGoogleMapUrl(place.latitude, place.longitude)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openGoogleMaps}
              className="inline-flex shrink-0 items-center justify-center gap-[5px] whitespace-nowrap rounded-full border border-[#E5E6E6] px-2.5 py-[7px] text-[11px] leading-4 text-[#006369] hover:bg-[#EBFBF9]"
            >
              Google Maps
              <ExternalLink className="size-[11px]" aria-hidden />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
